/**
 * @fileoverview Orders page composing filters and table under shared FilterProvider.
 * @module features/orders/pages/OrdersPage
 */

import { ReactElement, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import PageMeta from '@/shared/components/common/PageMeta'
import { Button, ScrollReveal } from '@/shared/components/ui'
import { useFilterMenuSlot } from '@/shared/contexts/FilterMenuSlotContext'
import { SalesFiltersExtension } from '@/shared/components/filters/extensions/SalesFiltersExtension'
import { OrdersTable } from '@/features/orders/components/OrdersTable'

/**
 * OrdersPage: top-level page for viewing and filtering orders data.
 * Mirrors PaymentsPage container and card styling for consistency.
 *
 * @component
 * @example
 * ```tsx
 * <OrdersPage />
 * ```
 */
export function OrdersPage(): ReactElement {
  const { t } = useTranslation('features-orders')
  const { setSlot, clearSlot } = useFilterMenuSlot()

  useEffect(() => {
    setSlot({
      content: <SalesFiltersExtension />,
      hideAggregation: true,
      hideProfitBasis: true,
      maxWidthClass: 'max-w-screen-2xl',
    })
    return () => clearSlot()
  }, [setSlot, clearSlot])

  return (
    <>
      <PageMeta title={t('table.title')} description={t('subtitle')} />
      <div className="min-h-screen">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 py-8 space-y-6">
          <ScrollReveal blur duration={0.3}>
            {/* Page header */}
            <div className="p-6 pb-3 flex items-center justify-between rounded-lg">
              <h1 className="text-2xl font-bold text-foreground">{t('table.title')}</h1>
              <Link to="/cogs">
                <Button variant="outline" size="sm" className="gap-2">
                  COGs Report
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
        </ScrollReveal>

        <ScrollReveal delay={0.1} blur duration={0.3}>
          {/* Main content card */}
            <div className="px-6 pb-6 bg-card rounded-3xl border border-border/15 flex-1 flex flex-col">
              <OrdersTable />
            </div>
          </ScrollReveal>
        </div>
      </div>
    </>
  )
}

export default OrdersPage
